import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Typography,
  Paper,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../../../supabaseClient';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.2)}`,
    overflow: 'hidden',
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`
  }
}));

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const TaskModal = ({ 
  open, 
  onClose, 
  task, 
  onSave, 
  users, 
  currentUser,
  mode = 'edit'
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: null,
    start_date: null,
    column_type: 'today',
    tags: [],
    estimated_hours: ''
  });
  
  const [newTag, setNewTag] = useState('');
  const [isMarkdownPreview, setIsMarkdownPreview] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    if (task && mode === 'edit') {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date ? dayjs(task.due_date) : null,
        start_date: task.start_date ? dayjs(task.start_date) : null,
        column_type: task.column_type || 'today',
        tags: task.tags || [],
        estimated_hours: task.estimated_hours || ''
      });
      setComments(task.comments || []);
      setAttachments(task.attachments || []);
      setCollaborators(task.collaborators || []);
    } else if (mode === 'create') {
              setFormData({
        title: '',
        description: '',
        priority: 'medium',
        due_date: null,
        start_date: null,
        column_type: task?.defaultColumn || 'today',
        tags: [],
        estimated_hours: ''
      });
      setComments([]);
      setAttachments([]);
      setCollaborators([]);
    }
  }, [task, mode, currentUser]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = () => {
    if (!formData.title.trim()) return;
    
    const taskData = {
      ...formData,
      due_date: formData.due_date ? formData.due_date.toISOString() : null,
      start_date: formData.start_date ? formData.start_date.toISOString() : null,
      id: task?.id,
      created_by: mode === 'create' ? currentUser?.id : task?.created_by,
      assigned_to: currentUser?.id // Always assign to current user
    };
    
    onSave(taskData);
    onClose();
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    const comment = {
      id: Date.now().toString(),
      user_id: currentUser?.id,
      comment: newComment,
      created_at: new Date().toISOString(),
      user: currentUser
    };
    
    setComments(prev => [...prev, comment]);
    setNewComment('');
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const attachment = {
        id: Date.now().toString() + Math.random(),
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        file: file,
        uploaded_by: currentUser?.id
      };
      setAttachments(prev => [...prev, attachment]);
    });
  };

  const assignedUser = users?.find(u => u.id === formData.assigned_to);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledDialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          pb: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                {mode === 'create' ? 'Create New Task' : 'Edit Task'}
              </Typography>
                             {collaborators.length > 0 && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                   <GroupIcon sx={{ fontSize: 14, color: 'success.main' }} />
                   <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                     Collaborative Task ({collaborators.length} collaborators)
                   </Typography>
                 </Box>
               )}
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderColor: theme.palette.error.main,
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Main Content */}
            <Box sx={{ 
              flex: 1, 
              p: 3, 
              borderRight: mode === 'edit' ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
            }}>
              <TextField
                fullWidth
                label="Task Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
                required
              />

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Description
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setIsMarkdownPreview(!isMarkdownPreview)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    {isMarkdownPreview ? 'Edit' : 'Preview'}
                  </Button>
                </Box>
                
                {isMarkdownPreview ? (
                  <Paper sx={{ 
                    p: 3, 
                    minHeight: 140, 
                    maxHeight: 220, 
                    overflow: 'auto',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    backgroundColor: alpha(theme.palette.grey[50], 0.5)
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {formData.description || '*No description*'}
                    </ReactMarkdown>
                  </Paper>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter task description (Markdown supported)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        '&:hover': {
                          borderColor: theme.palette.primary.main
                        }
                      }
                    }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <FormControl>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>

                <FormControl>
                  <InputLabel>Column</InputLabel>
                  <Select
                    value={formData.column_type}
                    label="Column"
                    onChange={(e) => handleInputChange('column_type', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="this_month">This Month</MenuItem>
                    <MenuItem value="later">Later</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <DateTimePicker
                  label="Start Date"
                  value={formData.start_date}
                  onChange={(date) => handleInputChange('start_date', date)}
                  renderInput={(params) => <TextField {...params} sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }} />}
                />
                
                <DateTimePicker
                  label="Due Date"
                  value={formData.due_date}
                  onChange={(date) => handleInputChange('due_date', date)}
                  renderInput={(params) => <TextField {...params} sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }} />}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, mb: 3 }}>
                <FormControl>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={currentUser?.id || ''}
                    label="Assigned To"
                    disabled={true} // Always disabled since tasks are personal
                    sx={{ borderRadius: 2 }}
                    renderValue={(value) => {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ProfileAvatar userId={currentUser?.id} user={currentUser} size={24} />
                          {currentUser?.full_name || 'Me'}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value={currentUser?.id || ''}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ProfileAvatar userId={currentUser?.id} user={currentUser} size={24} />
                        {currentUser?.full_name || 'Me'}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Est. Hours"
                  type="number"
                  value={formData.estimated_hours}
                  onChange={(e) => handleInputChange('estimated_hours', e.target.value)}
                  sx={{ 
                    width: 120,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }}
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Add tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                  <Button 
                    onClick={handleAddTag} 
                    disabled={!newTag.trim()}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                  Attachments
                </Typography>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    component="span"
                    startIcon={<AttachFileIcon />}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    Upload Files
                  </Button>
                </label>
                
                {attachments.length > 0 && (
                  <List dense>
                    {attachments.map((attachment) => (
                      <ListItem 
                        key={attachment.id}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main'
                          }}>
                            <AttachFileIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={attachment.file_name}
                          secondary={`${(attachment.file_size / 1024).toFixed(1)} KB`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => setAttachments(prev => prev.filter(a => a.id !== attachment.id))}
                            sx={{
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                              backgroundColor: alpha(theme.palette.error.main, 0.05),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* Comments Sidebar */}
            {mode === 'edit' && (
              <Box sx={{ 
                width: 320, 
                display: 'flex', 
                flexDirection: 'column',
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.4)})`
              }}>
                <Box sx={{ 
                  p: 3, 
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'info.main' }}>
                    Comments
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {comments.map((comment) => (
                    <Box key={comment.id} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ProfileAvatar
                          userId={comment.user_id}
                          user={comment.user}
                          size={28}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            color: theme.palette.grey[600]
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {comment.user?.full_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Paper sx={{ 
                        p: 2, 
                        backgroundColor: 'white',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.1)}`
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {comment.comment}
                        </ReactMarkdown>
                      </Paper>
                    </Box>
                  ))}
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  background: 'white'
                }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    sx={{ 
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      '&:hover': {
                        transform: 'scale(1.02)'
                      }
                    }}
                  >
                    Add Comment
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3,
                     background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.4)})`,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          <Button 
            onClick={onClose}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[100], 0.8)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.title.trim()}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                backgroundColor: alpha(theme.palette.grey[300], 0.5),
                color: alpha(theme.palette.grey[600], 0.8)
              },
              transition: 'all 0.2s ease'
            }}
          >
            {mode === 'create' ? 'Create Task' : 'Save Changes'}
          </Button>
        </DialogActions>
      </StyledDialog>
    </LocalizationProvider>
  );
};

export default TaskModal;